import axios from 'axios'
import axiosInstance from './axiosInstance'

// Base URL for auth API
const AUTH_API_URL = 'http://localhost:4000'

// Type for user data
export interface User {
  id: string
  firstName: string
  lastName: string
  email: string
  organization_id?: number
  organization_name?: string
  is_coach?: boolean
  iat?: number
  intercom_jwt?: string
}

// Create a separate axios instance for auth service
const authAxiosInstance = axios.create({
  baseURL: AUTH_API_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Add auth token to requests
authAxiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error),
)

/**
 * Get current user info
 * @returns User data or null if not authenticated
 */
export const getCurrentUser = async (): Promise<User | null> => {
  try {
    const response = await authAxiosInstance.get('/me')
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 403) {
      // User is not authenticated
      localStorage.removeItem('auth_token')
      return null
    }
    throw error
  }
}

/**
 * Check if user is authenticated
 * @returns Promise resolving to boolean indicating authentication status
 */
export const isAuthenticated = async (): Promise<boolean> => {
  try {
    const user = await getCurrentUser()
    return !!user
  } catch (error) {
    return false
  }
}

/**
 * Authenticate with Google token
 * @param token Google OAuth token
 * @returns User data if authentication successful
 */
export const authenticateWithGoogle = async (token: string): Promise<User> => {
  const response = await authAxiosInstance.post('/auth/google', { token })
  // Store the token returned from the backend
  localStorage.setItem('auth_token', response.data.token)
  return response.data.user
}

/**
 * Authenticate with Microsoft token
 * @param token Microsoft OAuth token
 * @returns User data if authentication successful
 */
export const authenticateWithMicrosoft = async (token: string): Promise<User> => {
  const response = await authAxiosInstance.post('/auth/microsoft', { token })
  // Store the token returned from the backend
  localStorage.setItem('auth_token', response.data.token)
  return response.data.user
}

/**
 * Send verification code to email
 * @param email User's email address
 * @returns Success status
 */
export const sendEmailCode = async (email: string): Promise<{ success: boolean }> => {
  const response = await authAxiosInstance.post('/auth/email/send-code', { email })
  return response.data
}

/**
 * Verify email code and authenticate
 * @param email User's email address
 * @param code 6-digit verification code
 * @returns User data if verification successful
 */
export const verifyEmailCode = async (email: string, code: string): Promise<User> => {
  const response = await authAxiosInstance.post('/auth/email/verify-code', { email, code })
  localStorage.setItem('auth_token', response.data.token)
  return response.data.user
}

/**
 * Logout user
 */
export const logout = (): void => {
  localStorage.removeItem('auth_token')
  window.location.href = '/auth'
}
